import { ChangeEvent, useState } from "react";
import { supabase } from "../lib/supabaseClient";

export type AIAutofillData = {
  reference_id: string;
  maintenance_date: string;
  cost: number;
  technician_name: string;
  date: string;
  maintenance_type: string;
  document_type: string;
  description: string;
};

type UploadStatus = "idle" | "loading" | "success" | "error" | "uploaded";

type AIAttachmentUploaderProps = {
  onAutofill?: (data: AIAutofillData, file: File | null) => void;
  assetTag: string;
  onSuccess?: () => void;
  className?: string;
};

const acceptedMimeTypes = ["image/jpeg", "image/png", "application/pdf"];

const initialFormData: AIAutofillData = {
  reference_id: "",
  maintenance_date: "",
  cost: 0,
  technician_name: "",
  date: "",
  maintenance_type: "",
  document_type: "Other",
  description: "",
};

function getReferenceIdLabel(docType: string) {
  switch (docType) {
    case "Invoice":
      return "Invoice ID / Receipt No";
    case "Warranty":
      return "Warranty ID / Serial No";
    case "Purchase Order":
      return "PO Number";
    case "User Manual":
      return "Manual Reference ID";
    case "Damage Photo":
      return "Photo Reference / Case ID";
    default:
      return "Reference ID";
  }
}

function getReferenceIdPlaceholder(docType: string) {
  switch (docType) {
    case "Invoice":
      return "e.g., INV-2026-001";
    case "Warranty":
      return "e.g., WARR-8921-A";
    case "Purchase Order":
      return "e.g., PO-5542";
    case "User Manual":
      return "e.g., MAN-DL-7420";
    case "Damage Photo":
      return "e.g., DMG-001";
    default:
      return "e.g., REF-12345";
  }
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Unable to read the selected file."));
        return;
      }

      resolve(reader.result);
    };

    reader.onerror = () => reject(new Error("Failed to read the selected file."));
    reader.readAsDataURL(file);
  });
}

function getBase64Payload(dataUrl: string) {
  return dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
}

function validateExtraction(data: unknown): AIAutofillData {
  if (!data || typeof data !== "object") {
    throw new Error("AI did not return extraction data.");
  }

  const value = data as Partial<AIAutofillData>;

  return {
    reference_id: typeof value.reference_id === "string" ? value.reference_id : "",
    maintenance_date: typeof value.maintenance_date === "string" ? value.maintenance_date : "",
    cost: typeof value.cost === "number" ? value.cost : 0,
    technician_name: typeof value.technician_name === "string" ? value.technician_name : "",
    date: typeof value.date === "string" ? value.date : "",
    maintenance_type: typeof value.maintenance_type === "string" ? value.maintenance_type : "",
    document_type: typeof value.document_type === "string" ? value.document_type : "Other",
    description: typeof value.description === "string" ? value.description : "",
  };
}

export default function AIAttachmentUploader({
  onAutofill,
  assetTag,
  onSuccess,
  className = "",
}: AIAttachmentUploaderProps) {
  const [formData, setFormData] = useState<AIAutofillData>(initialFormData);
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleChangeField = (key: keyof AIAutofillData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    setErrorMessage("");
    setStatus("idle");

    if (!file) return;

    setFileName(file.name);
    setSelectedFile(file);

    if (!acceptedMimeTypes.includes(file.type)) {
      setStatus("error");
      setErrorMessage("Upload a JPEG, PNG, or PDF maintenance receipt/log.");
      return;
    }

    try {
      setStatus("loading");

      const dataUrl = await readFileAsDataUrl(file);
      const { data, error } = await supabase.functions.invoke("document-intake", {
        body: {
          documentBase64: getBase64Payload(dataUrl),
          mimeType: file.type,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      const extraction = validateExtraction(data);

      setFormData(extraction);
      setStatus("success");
      onAutofill?.(extraction, file);
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "AI extraction failed.");
    } finally {
      event.target.value = "";
    }
  }

  async function handleConfirmUpload() {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setErrorMessage("");

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error("No active session. Please log in.");
      }

      // Resolve date correctly
      const resolvedDate = formData.date || formData.maintenance_date || null;
      const dbDate = resolvedDate === "" ? null : resolvedDate;

      const uniqueFileName = `${Date.now()}_${selectedFile.name}`;
      const filePath = `${assetTag}/${uniqueFileName}`;

      // 1. Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("asset_attachments")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // 2. Insert into DB
      const { error: dbError } = await supabase
        .from("asset_attachments")
        .insert([
          {
            asset_tag: assetTag,
            file_name: selectedFile.name,
            file_path: filePath,
            file_size: selectedFile.size,
            file_type: selectedFile.type,
            document_type: formData.document_type,
            description: formData.description,
            cost: formData.cost ? parseFloat(String(formData.cost)) : 0,
            date: dbDate,
            uploaded_by: user.id,
            reference_id: formData.reference_id,
          },
        ]);

      if (dbError) {
        throw new Error(dbError.message);
      }

      setStatus("uploaded");
      setFileName("");
      setSelectedFile(null);
      setFormData(initialFormData);
      onSuccess?.();
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <section className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 font-sans">AI Attachment Uploader</h3>
          </div>
          <p className="mt-1 text-sm text-slate-500 font-sans">
            Upload document (Invoice, Warranty, User Manual, etc.) and let AI auto-detect details and document type.
          </p>
        </div>

        <label className="inline-flex cursor-pointer items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 font-sans">
          {status === "loading" ? "Analyzing..." : "Upload Document"}
          <input
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileChange}
            disabled={status === "loading" || isUploading}
            className="sr-only"
          />
        </label>
      </div>

      {fileName && <p className="mt-3 text-xs text-slate-500 font-sans">Selected file: {fileName}</p>}

      {status === "loading" && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 font-sans">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          <span>AI is analyzing file contents and classifying document...</span>
        </div>
      )}

      {status === "error" && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 font-sans">
          {errorMessage}
        </div>
      )}

      {status === "uploaded" && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 font-sans flex items-center justify-between">
          <span>Attachment uploaded successfully!</span>
          <button 
            type="button" 
            onClick={() => setStatus("idle")} 
            className="text-xs font-semibold text-green-800 hover:text-green-950 underline"
          >
            Upload another
          </button>
        </div>
      )}

      {status === "success" && onAutofill && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50/50 p-4 text-sm text-green-800 font-sans flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-ping" />
            <span>AI scanned and autofilled fields! File attached: <strong>{fileName}</strong></span>
          </div>
          <button
            type="button"
            onClick={() => {
              setStatus("idle");
              setFileName("");
              setSelectedFile(null);
              setFormData(initialFormData);
              onAutofill(initialFormData, null);
            }}
            className="text-xs font-semibold text-red-600 hover:text-red-800 underline"
          >
            Remove file
          </button>
        </div>
      )}

      {status === "success" && !onAutofill && (
        <div className="mt-4 space-y-4 font-sans">
          <div className="rounded-md border border-yellow-200 bg-yellow-50/40 p-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-yellow-800 mb-3">
              AI Auto-Detected Information (Review & Confirm)
            </h4>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase">Document Type</label>
                <select
                  value={formData.document_type}
                  onChange={(e) => handleChangeField("document_type", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  <option value="Invoice">Invoice / Receipt</option>
                  <option value="Warranty">Warranty</option>
                  <option value="User Manual">User Manual</option>
                  <option value="Damage Photo">Damage Photo</option>
                  <option value="Purchase Order">Purchase Order</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase">
                  {getReferenceIdLabel(formData.document_type)}
                </label>
                <input
                  type="text"
                  value={formData.reference_id}
                  onChange={(e) => handleChangeField("reference_id", e.target.value)}
                  placeholder={getReferenceIdPlaceholder(formData.document_type)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase">Date</label>
                <input
                  type="date"
                  value={formData.date || formData.maintenance_date}
                  onChange={(e) => handleChangeField("date", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase">Cost (if applicable)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => handleChangeField("cost", parseFloat(e.target.value) || 0)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 uppercase">
                  Description / notes / crucial details
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChangeField("description", e.target.value)}
                  placeholder="Summary of document details, invoice items..."
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3 border-t border-yellow-200/50 pt-4">
              <button
                type="button"
                onClick={() => {
                  setStatus("idle");
                  setFileName("");
                  setSelectedFile(null);
                  setFormData(initialFormData);
                }}
                disabled={isUploading}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmUpload}
                disabled={isUploading}
                className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-50"
              >
                {isUploading && (
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {isUploading ? "Uploading..." : "Confirm & Upload Attachment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
